import React from 'react';
import ChatBubble from '@amzn/awsui-chat-components-console/chat-bubble';
import Avatar from '@amzn/awsui-chat-components-console/avatar';
import SupportPromptGroup from '@amzn/awsui-chat-components-console/support-prompt-group';
import * as tokens from '@amzn/awsui-design-tokens';
import MarkdownRenderer from './MarkdownRenderer';
import { WorkflowAssistantAvatar } from './WorkflowAssistantAvatar';
import { ASSISTANT_DISPLAY_NAME } from './constants';
import ChatCopyButton from './ChatCopyButton';
import type { Message } from './useChatbotLogic';

interface MessageListProps {
	messages: Message[];
	isTyping: boolean;
	showSuggestions: boolean;
	showWelcome: boolean;
	username: string | null;
	userPhotoUrl: string | null;
	promptItems: Array<{ text: string; id: string }>;
	messagesEndRef: React.RefObject<HTMLDivElement>;
	handleSuggestionClick: (suggestion: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({
	messages,
	isTyping,
	showSuggestions,
	showWelcome,
	username,
	userPhotoUrl,
	promptItems,
	messagesEndRef,
	handleSuggestionClick,
}) => (
	<>
		{messages.map((message, index) => {
			const isLatestBotMessage = message.sender === 'bot' && index === messages.length - 1;
			const isUser = message.sender === 'user';
			const time = new Date(message.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

			if (isUser) {
				return (
					<div key={message.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
						<div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', maxWidth: '85%' }}>
							<div className="chatbot-user-bubble" style={{
								padding: '10px 14px',
								borderRadius: tokens.borderRadiusContainer,
								fontSize: tokens.fontSizeBodyM,
								color: tokens.colorTextBodyDefault,
								lineHeight: '1.5',
								wordBreak: 'break-word' as const,
							}}>
								<MarkdownRenderer content={message.text} isTyping={false} />
							</div>
							<div style={{ flexShrink: 0 }}>
								<Avatar
									ariaLabel="Avatar of you"
									tooltipText={username || 'You'}
									color="default"
									imgUrl={userPhotoUrl || undefined}
									initials={username ? username.charAt(0).toUpperCase() : 'U'}
								/>
							</div>
						</div>
						<span style={{ fontSize: '11px', color: tokens.colorTextBodySecondary, marginTop: '2px', paddingRight: '44px' }}>
							{time}
						</span>
					</div>
				);
			}

			return (
				<div key={message.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
					<div style={{ maxWidth: '85%' }}>
						<ChatBubble
							type="incoming"
							ariaLabel={`${ASSISTANT_DISPLAY_NAME} at ${time}`}
							avatar={<WorkflowAssistantAvatar loading={isLatestBotMessage && message.isTyping} />}
							actions={!message.isTyping ? <ChatCopyButton text={message.fullContent || message.text} /> : undefined}
							showLoadingBar={isLatestBotMessage && message.isTyping}
						>
							{message.isTyping && !message.text.trim() ? (
								<span style={{ fontSize: tokens.fontSizeBodyM, color: tokens.colorTextBodySecondary, fontStyle: 'italic' }}>
									Generating reply…
								</span>
							) : (
								<MarkdownRenderer content={message.text} isTyping={message.isTyping} />
							)}
						</ChatBubble>
					</div>
					<span style={{ fontSize: '11px', color: tokens.colorTextBodySecondary, marginTop: '2px', paddingLeft: '44px' }}>
						{time}
					</span>
				</div>
			);
		})}

		{/* Typing indicator while waiting for first token */}
		{isTyping && !messages.some(m => m.isTyping) && (
			<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
				<div style={{ maxWidth: '85%' }}>
					<ChatBubble
						type="incoming"
						ariaLabel={`${ASSISTANT_DISPLAY_NAME} is thinking`}
						avatar={<WorkflowAssistantAvatar loading={true} />}
					>
						<span style={{ fontSize: tokens.fontSizeBodyM, color: tokens.colorTextBodySecondary, fontStyle: 'italic' }}>
							Generating reply…
						</span>
					</ChatBubble>
				</div>
			</div>
		)}

		{/* Suggestion chips */}
		{showSuggestions && !showWelcome && messages.filter(m => m.sender === 'user').length === 0 && (
			<SupportPromptGroup
				ariaLabel="Suggested prompts"
				onItemClick={({ detail }) => handleSuggestionClick(detail.id)}
				items={promptItems}
			/>
		)}

		<div ref={messagesEndRef} />
	</>
);
